/**
 * /field/downhole/assemblies/:id — one string, as a field hand needs it: what
 * is in it, its runs, and the next thing to do with it.
 *
 *   in_hole     Log run (open, completed, or mobilized-not-run) · Close run
 *   laid_down   Log run (completed or not-run) · Tear down
 *   ready       nothing to do here — dispatch is the office's
 *
 * The run dialogs are the office's own (RunDialogs.tsx): one set of rules for
 * what a run may carry, tested once. Closing here can also flag a tool lost in
 * hole, because the field is where that is found out.
 *
 * Tear down waits for every run to be closed: an open run on a laid-down string
 * is not something the API makes, but if it is there the checklist is not the
 * next step, closing it is.
 */
import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ClipboardList, Plus, Square } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { useDownholeAssembly, useDownholeAssemblyRuns } from '../../../hooks/useDownhole';
import { formatInstantDay } from '../../../lib/calendar-date';
import { canBuildDownhole } from '../../../lib/downhole-roles';
import { DH_ASSEMBLY_STATUS_SKIN, DH_CATEGORY_LABEL, dhLabel } from '../../../lib/downhole-labels';
import { formatDh } from '../../../lib/downhole-units';
import { DhBadge, RunStatePill } from '../DownholeBits';
import { CloseRunDialog, RunFormDialog } from '../RunDialogs';
import { lengthFromBottom, positionNote, topFirst } from '../toolString';
import { FIELD_PRIMARY, FIELD_SECONDARY, FieldCard, FieldEmpty, FieldQueryState, FieldSectionTitle, FieldTitle } from './FieldBits';

export default function FieldAssemblyScreen() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const canBuild = canBuildDownhole(user?.role);
  const query = useDownholeAssembly(id);
  const runsQuery = useDownholeAssemblyRuns(id);
  const [dialog, setDialog] = useState<'log' | 'close' | null>(null);
  const asm = query.data?.data ?? null;
  const units = query.data?.meta.unit_system ?? null;
  const tz = query.data?.meta.time_zone ?? null;
  const runs = runsQuery.data?.data ?? [];
  const openRun = runs.find((r) => r.run_state === 'open') ?? null;
  const runsKnown = !!runsQuery.data;
  const fromBottom = lengthFromBottom(asm?.components ?? []);

  return (
    <div data-testid="downhole-field-assembly">
      <FieldQueryState query={query} subject="this assembly">
        {!asm ? (
          <FieldEmpty>This assembly does not exist in your company.</FieldEmpty>
        ) : (
          <>
            <FieldTitle eyebrow={asm.assembly_number} title={asm.name}>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <DhBadge map={DH_ASSEMBLY_STATUS_SKIN} value={asm.status} />
                <span className="text-sm text-fieldiq-text">{[asm.well_name, asm.rig_name].filter(Boolean).join(' · ') || '—'}</span>
              </div>
              {asm.customer_name && <p className="mt-1 text-xs text-fieldiq-muted">{asm.customer_name}</p>}
            </FieldTitle>

            {canBuild && (asm.status === 'in_hole' || asm.status === 'laid_down') && (
              // The actions wait for the runs: whether a run is open decides
              // which of them is next, and a guess would offer the wrong one.
              <div className="space-y-2">
                {!runsKnown ? null : openRun ? (
                  <button type="button" className={FIELD_PRIMARY} onClick={() => setDialog('close')}>
                    <Square className="size-4" aria-hidden="true" />
                    Close run {openRun.run_number ?? ''}
                  </button>
                ) : (
                  <>
                    {asm.status === 'laid_down' && (
                      <Link to={`/field/downhole/assemblies/${asm.id}/teardown`} className={FIELD_PRIMARY}>
                        <ClipboardList className="size-4" aria-hidden="true" />
                        Tear down
                      </Link>
                    )}
                    <button type="button" className={asm.status === 'laid_down' ? FIELD_SECONDARY : FIELD_PRIMARY} onClick={() => setDialog('log')}>
                      <Plus className="size-4" aria-hidden="true" />
                      Log run
                    </button>
                  </>
                )}
              </div>
            )}
            {asm.status === 'ready' && (
              <p className="text-sm text-fieldiq-text">Built and waiting for dispatch. Runs are logged once it is in the hole.</p>
            )}

            <FieldSectionTitle>Runs</FieldSectionTitle>
            <FieldQueryState query={runsQuery} subject="this assembly's runs">
              {runs.length === 0 ? (
                <FieldEmpty>No runs have been logged for this assembly.</FieldEmpty>
              ) : (
                <FieldCard>
                  <ul className="divide-y divide-fieldiq-border">
                    {runs.map((r) => (
                      <li key={r.id} className="px-4 py-3 text-sm">
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-white">Run {r.run_number ?? '—'}</span>
                          <RunStatePill state={r.run_state} />
                          {r.lih_flag && (
                            <span className="rounded-fi-pill border border-black/20 bg-fi-signal-red px-2 py-0.5 text-[0.62rem] font-black uppercase text-fi-signal-ink">
                              Lost in hole
                            </span>
                          )}
                        </div>
                        <p className="mt-0.5 text-xs text-fieldiq-text">
                          In {formatInstantDay(r.run_in_at, tz) ?? '—'} · Out {formatInstantDay(r.run_out_at, tz) ?? '—'} · {formatDh(r.footage_drilled, 'length', units, { digits: 0 })}
                        </p>
                      </li>
                    ))}
                  </ul>
                </FieldCard>
              )}
            </FieldQueryState>

            <FieldSectionTitle>
              The string · {asm.components.length} · top first
            </FieldSectionTitle>
            {asm.components.length === 0 ? (
              <FieldEmpty>There are no tools in this string now.</FieldEmpty>
            ) : (
              <FieldCard>
                <ol className="divide-y divide-fieldiq-border" aria-label="Tool string, top of the string first">
                  {topFirst(asm.components).map((m) => (
                    <li key={m.assembly_component_id} className="flex items-start gap-3 px-4 py-3 text-sm">
                      <span className="w-12 shrink-0 text-right font-mono text-fieldiq-muted">
                        {m.position}
                        {positionNote(m.position, asm.components.length) && (
                          <span className="block font-sans text-[0.6rem] font-black uppercase tracking-[0.08em]">
                            {positionNote(m.position, asm.components.length)}
                          </span>
                        )}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block font-mono font-extrabold text-fieldiq-accent-signal">{m.serial_number}</span>
                        <span className="block text-xs text-fieldiq-text">
                          {dhLabel(DH_CATEGORY_LABEL, m.category)} · OD {formatDh(m.od, 'diameter', units)}
                        </span>
                        <span className="block text-xs text-fieldiq-muted">
                          Length {formatDh(m.length_ft, 'length', units)} · from bottom {formatDh(fromBottom.get(m.assembly_component_id) ?? null, 'length', units)}
                        </span>
                        {m.condition_on_return === 'lost_in_hole' && <span className="block text-xs font-bold text-fieldiq-text">Lost in hole</span>}
                      </span>
                    </li>
                  ))}
                </ol>
              </FieldCard>
            )}

            {canBuild && (
              <>
                <RunFormDialog open={dialog === 'log'} onClose={() => setDialog(null)} assembly={asm} units={units} tz={tz} />
                {openRun && (
                  <CloseRunDialog open={dialog === 'close'} onClose={() => setDialog(null)} run={openRun} units={units} tz={tz} assembly={asm} />
                )}
              </>
            )}
          </>
        )}
      </FieldQueryState>
    </div>
  );
}
